import React from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData, OrderPageTypes, RedeemableOnItemInput } from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import { activeTickets, findMultipleTicketsUiObject } from '../../../common/multipleTicketsUtils';
import { WorkshopCircle } from '../../../common/ui/WorkshopCircle';
import { getOrderPages } from '../../../utils/AccountUtils';
import { useOrderPageLocationGroups } from '../../../utils/useOrderPageLocationGroups';

const TYPE_SORT_ORDER: Record<OrderPageTypes, number> = {
  [OrderPageTypes.EVENT]: 0,
  [OrderPageTypes.COURSE]: 1,
  [OrderPageTypes.PRIVATE]: 2,
};

const TYPE_LABEL_KEY: Record<OrderPageTypes, string> = {
  [OrderPageTypes.EVENT]: 'editOrderPage.orderPageTypeEvent',
  [OrderPageTypes.COURSE]: 'editOrderPage.orderPageTypeCourse',
  [OrderPageTypes.PRIVATE]: 'editOrderPage.orderPageTypePrivate',
};

const sortPagesByType = (pages: OrderPageData[]): OrderPageData[] =>
  [...pages].sort((a, b) => TYPE_SORT_ORDER[a.order_page_type] - TYPE_SORT_ORDER[b.order_page_type]);

type RedeemableOnMultiSelectProps = {
  value: RedeemableOnItemInput[];
  onChange: (items: RedeemableOnItemInput[]) => void;
  account: AccountData;
  disabled?: boolean;
  guestPassQuota?: number;
  orderPageTypeFilter?: OrderPageTypes[];
};

export const RedeemableOnMultiSelect = ({
  value,
  onChange,
  account,
  disabled,
  guestPassQuota,
  orderPageTypeFilter,
}: RedeemableOnMultiSelectProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const orderPages = orderPageTypeFilter
    ? getOrderPages(account).filter((p) => orderPageTypeFilter.includes(p.order_page_type))
    : getOrderPages(account);

  const itemByPageId = new Map(value.map((item) => [item.order_page_id, item]));

  const showAllowGuest = !!guestPassQuota && guestPassQuota > 0;

  const { groupByLocation, groupPagesByLocation, locationLabel } = useOrderPageLocationGroups(account);

  const handleToggle = (pageId: string, wasChecked: boolean) => {
    if (wasChecked) {
      onChange(value.filter((item) => item.order_page_id !== pageId));
    } else {
      onChange([...value, { order_page_id: pageId, ...(showAllowGuest ? { allow_guest: true } : {}) }]);
    }
  };

  const handleLimitChange = (pageId: string, raw: string) => {
    const parsed = parseInt(raw, 10);
    const limit = raw === '' || Number.isNaN(parsed) || parsed < 1 ? undefined : parsed;
    onChange(value.map((item) => (item.order_page_id === pageId ? { ...item, limit } : item)));
  };

  const handleTicketTypeChange = (pageId: string, ticketTypeId: string | undefined) => {
    onChange(value.map((item) => (item.order_page_id === pageId ? { ...item, ticket_type_id: ticketTypeId } : item)));
  };

  const handleAllowGuestChange = (pageId: string, allowGuest: boolean) => {
    onChange(value.map((item) => (item.order_page_id === pageId ? { ...item, allow_guest: allowGuest } : item)));
  };

  const renderPageRow = (page: OrderPageData) => {
    const item = itemByPageId.get(page.id);
    const checked = !!item;
    const multipleTicketsUi = findMultipleTicketsUiObject(page);
    const tickets = multipleTicketsUi ? activeTickets(multipleTicketsUi) : [];
    return (
      <Grid key={page.id} container alignItems="center" gap={1} flexWrap="nowrap" mb={0.5}>
        <FormControlLabel
          sx={{ marginInlineEnd: 0, flex: 'none' }}
          control={<Checkbox checked={checked} disabled={disabled} onChange={() => handleToggle(page.id, checked)} />}
          label={
            <Grid container flexWrap="nowrap" gap={1} alignItems="center">
              <WorkshopCircle account={account} orderPageId={page.id} language={language} />
              <Typography>{page.label[language]}</Typography>
              <Typography variant="caption" color="text.secondary">
                [{t(TYPE_LABEL_KEY[page.order_page_type])}]
              </Typography>
            </Grid>
          }
        />
        {checked && multipleTicketsUi && (
          <Select
            size="small"
            displayEmpty
            disabled={disabled}
            value={item?.ticket_type_id ?? ''}
            onChange={(e) => handleTicketTypeChange(page.id, e.target.value || undefined)}
            sx={{ width: 200 }}>
            <MenuItem value="">
              <em>{t('passes.select_ticket_type')}</em>
            </MenuItem>
            {tickets.map((ticket) => (
              <MenuItem key={ticket.id} value={ticket.id}>
                {ticket.label[language] || ticket.id}
              </MenuItem>
            ))}
          </Select>
        )}
        {checked && (
          <TextField
            size="small"
            type="number"
            disabled={disabled}
            placeholder={t('passes.redeemable_on_no_limit')}
            value={item?.limit ?? ''}
            onChange={(e) => handleLimitChange(page.id, e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    {t('passes.redeemable_on_limit_adornment')}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{ width: 160 }}
          />
        )}
        {checked && showAllowGuest && (
          <FormControlLabel
            sx={{ marginInlineEnd: 0 }}
            control={
              <Checkbox
                checked={item?.allow_guest ?? false}
                disabled={disabled}
                onChange={(e) => handleAllowGuestChange(page.id, e.target.checked)}
              />
            }
            label={t('passes.allow_guest_on_activity')}
          />
        )}
      </Grid>
    );
  };

  if (groupByLocation) {
    return (
      <>
        {groupPagesByLocation(orderPages).map((group, idx) => (
          <Grid key={group.location?.id || 'other'} container flexDirection="column" mt={idx === 0 ? 0 : 0.75}>
            <Typography variant="caption" color="text.secondary">
              {locationLabel(group.location)}
            </Typography>
            <FormGroup>{sortPagesByType(group.pages).map(renderPageRow)}</FormGroup>
          </Grid>
        ))}
      </>
    );
  }

  return <FormGroup>{sortPagesByType(orderPages).map(renderPageRow)}</FormGroup>;
};
